/**
 * Ad Size Configuration Store
 *
 * Pinia store for managing ad size configurations.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AdSizeConfig, Bounds, AspectCounts, AdSizeSummary } from '@/types/adSizeConfig'
import { evaluateCondition } from '@/services/conditionEvaluator'
import { usePlacementStore } from '@/stores/placement'

export const useAdSizeConfigStore = defineStore('adSizeConfig', () => {
    // ==========================================================================
    // STATE
    // ==========================================================================
    const config = ref<AdSizeConfig | null>(null)
    const configCache = ref<Map<string, AdSizeConfig>>(new Map())
    const isLoading = ref(false)
    const error = ref<Error | null>(null)

    // ==========================================================================
    // COMPUTED - Basic State
    // ==========================================================================
    const isReady = computed(() => config.value !== null)

    // ==========================================================================
    // COMPUTED - Canvas
    // ==========================================================================
    const canvasSize = computed(() => config.value?.canvas ?? { width: 0, height: 0 })

    // ==========================================================================
    // COMPUTED - Features
    // ==========================================================================
    const maxProducts = computed(() => config.value?.features.maxProducts ?? 0)
    const availablePlacementTypes = computed(() => config.value?.features.availablePlacements ?? [])
    const supportsDuotone = computed(() => config.value?.features.supportsDuotone ?? false)
    const supportsAutofix = computed(() => config.value?.features.supportsAutofix ?? false)

    // ==========================================================================
    // COMPUTED - Product Bounds
    // ==========================================================================
    const defaultPlacementArea = computed(() => config.value?.product.bounds.default ?? { left: 0, top: 0, right: 0, bottom: 0 })
    const frontBaseBottom = computed(() => config.value?.product.baselines.front ?? 0)
    const topBaseBottom = computed(() => config.value?.product.baselines.top ?? 0)
    const minLeftConstraint = computed(() => config.value?.product.constraints.minLeft ?? 0)

    // ==========================================================================
    // COMPUTED - Text
    // ==========================================================================
    const maximumAllowedWidth = computed(() => config.value?.text.headline.width.max ?? 571)
    const minimumAllowedWidth = computed(() => config.value?.text.headline.width.min ?? 0)
    const estimatedSingleLineHeight = computed(() => config.value?.text.headline.lineHeight ?? 72.32)
    const headlineCharLimits = computed(() => ({
        min: config.value?.text.headline.ai.min ?? 10,
        max: config.value?.text.headline.ai.max ?? 18,
    }))
    const headlineUILimits = computed(() => ({
        hardLimit: config.value?.text.headline.ui.hardLimit ?? 25,
        recommendedLimit: config.value?.text.headline.ui.recommended ?? 20,
        warningThreshold: config.value?.text.headline.ui.warning ?? 21,
    }))
    const subheadlineHardLimit = computed(() => config.value?.text.subheadline.ui.hardLimit ?? 50)
    const subheadlineMaxWidth = computed(() => config.value?.text.subheadline.width.max ?? 571)

    // ==========================================================================
    // COMPUTED - Background
    // ==========================================================================
    const floorEdge = computed(() => config.value?.background.duotone.floorY ?? 559)

    // ==========================================================================
    // ACTIONS - Loading
    // ==========================================================================
    async function fetchAvailableAdSizes(): Promise<AdSizeSummary[]> {
        const placementStore = usePlacementStore()
        return placementStore.placements.flatMap((placement) => {
            const creativeSetConfig = placement.creativeSetConfiguration
            const creativeType = placement.creativeType
            if (!creativeSetConfig || !creativeType) return []

            return [{
                id: creativeSetConfig.meta.id,
                creativeType: creativeType,
                displayName: creativeSetConfig.meta.displayName,
                description: creativeSetConfig.meta.description ?? "",
                width: creativeSetConfig.canvas.width,
                height: creativeSetConfig.canvas.height
            }]
        })
    }

    async function loadConfig(id: string): Promise<AdSizeConfig> {
        // Check cache
        const placementStore = usePlacementStore()
        const cached = configCache.value.get(id)
        if (cached) {
            config.value = cached
            return cached
        }

        isLoading.value = true
        error.value = null

        try {
            const placements = placementStore.placements.filter(placement => placement.creativeType === "SILB" || placement.creativeType === "L2 Banner")
            const fetched = placements.find((placement) => placement.creativeSetConfiguration.meta.id == id)?.creativeSetConfiguration
            if (!fetched) {
                throw new Error("CreativeSetConfiguration not loaded yet")
            }
            configCache.value.set(id, fetched)
            config.value = fetched
            return fetched
        } catch (e) {
            error.value = e as Error
            throw e
        } finally {
            isLoading.value = false
        }
    }

    function clearCache(): void {
        configCache.value.clear()
        config.value = null
    }

    // ==========================================================================
    // ACTIONS - Getters with params (can't be computed)
    // ==========================================================================
    function getMaximumPlacementArea(isFoodCategory: boolean = false): Bounds {
        const bounds = config.value?.product.bounds
        if (!bounds) return { left: 0, top: 0, right: 0, bottom: 0 }
        return isFoodCategory ? { ...bounds.maxProductFood } : { ...bounds.maxProduct }
    }

    function getNamedBounds(name: string): Bounds {
        const bounds = config.value?.product.bounds[name]
        if (!bounds) {
            console.warn(`Named bounds '${name}' not found, using default`)
            return { ...defaultPlacementArea.value }
        }
        return { ...bounds }
    }

    function getValidPlacementArea(hasInvisibleLayers: boolean = false): Bounds {
        const autofix = config.value?.autofix
        if (!autofix) return { left: 0, top: 0, right: 0, bottom: 0 }
        return hasInvisibleLayers ? { ...autofix.validBoundsWithOverlay } : { ...autofix.validBounds }
    }

    function getSubheadlineCharLimits(discountEnabled: boolean): { min: number; max: number } {
        const sub = config.value?.text.subheadline
        if (!sub) return { min: 0, max: 0 }
        return discountEnabled
            ? { min: sub.withDiscount.min, max: sub.withDiscount.max }
            : { min: sub.withoutDiscount.min, max: sub.withoutDiscount.max }
    }

    // ==========================================================================
    // ACTIONS - Rules
    // ==========================================================================
    function getBasePlacements(productCount: number): string[] {
        const ruleSet = config.value?.rules.elimination.find((r) => r.productCount === productCount)
        return ruleSet?.basePlacements ?? []
    }

    function getDisallowedPlacements(productCount: number, aspectCounts: AspectCounts): string[] {
        const ruleSet = config.value?.rules.elimination.find((r) => r.productCount === productCount)
        if (!ruleSet) return []

        const disallowed: Set<string> = new Set()
        for (const rule of ruleSet.rules) {
            if (evaluateCondition(rule.when, aspectCounts)) {
                rule.disallow.forEach((p) => disallowed.add(p))
            }
        }
        return Array.from(disallowed)
    }

    function getAvailablePlacements(productCount: number, aspectCounts: AspectCounts): string[] {
        const base = getBasePlacements(productCount)
        const disallowed = getDisallowedPlacements(productCount, aspectCounts)
        return base.filter((p) => !disallowed.includes(p))
    }

    return {
        // State
        config,
        isLoading,
        error,
        isReady,

        // Computed - Canvas
        canvasSize,

        // Computed - Features
        maxProducts,
        availablePlacementTypes,
        supportsDuotone,
        supportsAutofix,

        // Computed - Product
        defaultPlacementArea,
        frontBaseBottom,
        topBaseBottom,
        minLeftConstraint,

        // Computed - Text
        maximumAllowedWidth,
        minimumAllowedWidth,
        estimatedSingleLineHeight,
        headlineCharLimits,
        headlineUILimits,
        subheadlineHardLimit,
        subheadlineMaxWidth,

        // Computed - Background
        floorEdge,

        // Actions - Loading
        fetchAvailableAdSizes,
        loadConfig,
        clearCache,

        // Actions - Getters with params
        getMaximumPlacementArea,
        getNamedBounds,
        getValidPlacementArea,
        getSubheadlineCharLimits,

        // Actions - Rules
        getBasePlacements,
        getDisallowedPlacements,
        getAvailablePlacements
    }
})
